import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function AccountPage() {
  const { user, logout, loading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [tradeIns, setTradeIns] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/tradein/mine").then((r) => setTradeIns(r.data)).catch(() => {});
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#000000]/60">
        Loading…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div data-testid="account-page" className="bg-[#ffffff] min-h-screen">
      <section className="border-b border-[#000000]/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="overline">Maison register</p>
            <h1 className="font-serif text-4xl md:text-5xl mt-3 leading-tight">
              {user.name}
            </h1>
            <p className="text-[#000000]/60 mt-2">{user.email}</p>
          </div>
          <div className="flex items-center gap-3">
            {user.vip && (
              <span className="text-[10px] tracking-[0.28em] uppercase text-[#000000] bg-[#ffffff] px-3 py-2">
                VIP · {user.vip_ticket || "ACTIVE"}
              </span>
            )}
            <button
              data-testid="logout-button"
              onClick={logout}
              className="btn-secondary-dark"
            >
              Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 grid md:grid-cols-2 gap-12">
        <div data-testid="orders-section">
          <h2 className="overline">Orders</h2>
          <div className="mt-4 space-y-3">
            {orders.length === 0 ? (
              <p className="text-[#000000]/55 text-sm">
                No orders yet.{" "}
                <Link to="/shop" className="text-[#000000] underline">
                  Browse the maison
                </Link>
                .
              </p>
            ) : (
              orders.map((o) => (
                <article
                  key={o.order_id}
                  className="border border-[#000000]/10 p-5"
                  data-testid={`order-${o.order_id}`}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-[#000000]">{o.order_id}</span>
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#000000]/55">
                      {o.status}
                    </span>
                  </div>
                  <p className="text-sm text-[#000000]/70 mt-1">
                    {(o.lines || [])
                      .map((l) => `${l.title} × ${l.quantity}`)
                      .join(" · ")}
                  </p>
                  <p className="text-sm text-[#000000] mt-2">
                    ${o.totals?.total?.toFixed(2)}
                  </p>
                </article>
              ))
            )}
          </div>
        </div>

        <div data-testid="tradeins-section">
          <h2 className="overline">Trade-Ins</h2>
          <div className="mt-4 space-y-3">
            {tradeIns.length === 0 ? (
              <p className="text-[#000000]/55 text-sm">
                No trade-ins yet.{" "}
                <Link to="/vip" className="text-[#000000] underline">
                  Start a Trade-In
                </Link>
                .
              </p>
            ) : (
              tradeIns.map((t) => (
                <article
                  key={t.tradein_id}
                  className="border border-[#000000]/10 p-5"
                  data-testid={`tradein-${t.tradein_id}`}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-mono text-[#000000]">{t.tradein_id}</span>
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#000000]/55">
                      {t.status}
                    </span>
                  </div>
                  <p className="text-sm text-[#000000]/70 mt-1">
                    {t.condition.replace("_", " ")} · ${t.jacket_value.toFixed(0)} credit
                  </p>
                </article>
              ))
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
